
import { 
  toast as sonnerToast,
  type ToastT 
} from "sonner";

type ToastProps = {
  title?: string;
  description?: string;
  variant?: "default" | "destructive" | "success" | "warning";
  action?: React.ReactNode;
} & Omit<Parameters<typeof sonnerToast>[1], "action" | "description">;

/**
 * This hook provides a toast function that integrates with sonner.
 * It exists to provide a consistent API across the application and to allow for
 * easier migration to other toast libraries if needed.
 */
export function useToast() {
  const toast = ({ title, description, variant, ...props }: ToastProps) => {
    // Map our variant to sonner's type
    if (variant === "destructive") {
      return sonnerToast.error(title, {
        description,
        ...props
      });
    } else if (variant === "success") {
      return sonnerToast.success(title, {
        description,
        ...props
      });
    } else if (variant === "warning") {
      return sonnerToast.warning(title, {
        description,
        ...props
      });
    } else {
      return sonnerToast(title, {
        description,
        ...props
      });
    }
  };

  return {
    toast,
    error: (message: string) => sonnerToast.error(message),
    success: (message: string) => sonnerToast.success(message),
    info: (message: string) => sonnerToast.info(message),
    warning: (message: string) => sonnerToast.warning(message),
    // Re-export other sonner methods
    promise: sonnerToast.promise,
    loading: sonnerToast.loading,
    custom: sonnerToast.custom,
    dismiss: sonnerToast.dismiss
  };
}

// Also export a simpler version for direct import
export const toast = {
  // Basic toast with title and description
  toast: (props: ToastProps) => {
    if (props.variant === "destructive") {
      return sonnerToast.error(props.title, {
        description: props.description,
        ...props,
      });
    } else if (props.variant === "success") {
      return sonnerToast.success(props.title, {
        description: props.description,
        ...props,
      });
    } else if (props.variant === "warning") {
      return sonnerToast.warning(props.title, {
        description: props.description,
        ...props,
      });
    } else {
      return sonnerToast(props.title, {
        description: props.description,
        ...props,
      });
    }
  },
  // Regular toast with just a message
  default: (message: string) => sonnerToast(message),
  error: (message: string) => sonnerToast.error(message),
  success: (message: string) => sonnerToast.success(message),
  info: (message: string) => sonnerToast.info(message),
  warning: (message: string) => sonnerToast.warning(message),
  // Re-export other sonner methods
  promise: sonnerToast.promise,
  loading: sonnerToast.loading,
  custom: sonnerToast.custom,
  dismiss: sonnerToast.dismiss
};
